import Immutable from 'seamless-immutable';
import moment from 'moment-timezone';
import _ from 'lodash';
import RefData from 'ref-data';

// polling interval in milliseconds
const pollingInterval = {
  S:  5000,
  M:  10000,
  L:  30000,
  XL: 60000,
};

const exportTaskProgress = {
  S: {
    header: 'Generating export...',
    byline: 'Estimated time < 1 minute',
    label:  'Check back soon or keep this page open and your file will display when ready.',
  },
  M: {
    header: 'Generating export...',
    byline: 'Estimated time < 1 hour',
    label:
      'We will send an email to let you know when the export is ready, or you can leave this page open and your file will appear when ready.',
  },
  L: {
    header: 'Generating export...',
    byline: 'Estimated time < 2 hours',
    label:
      'We will send an email to let you know when the export is ready, or you can leave this page open and your file will appear when ready.',
  },
  XL: {
    header: 'Generating export...',
    byline: 'Estimated time < 5 hours',
    label:
      'We will send an email to let you know when the export is ready, or you can leave this page open and your file will appear when ready.',
  },
};

/**
 * Entry Export
 * @typedef {object} Entry Export
 *
 * @property {string} allowExport
 * @property {string} currentNumRows
 * @property {string} dateCreated
 * @property {string} dateFinish
 * @property {string} exportId
 * @property {string} fileUrl
 * @property {string} id
 * @property {string} numRows
 * @property {string} password
 * @property {string} processTime
 * @property {string} size
 * @property {string} status
 * @property {string} typeRef
 */

function create(exportTask = {}) {
  const result = _.clone(exportTask);

  if (!_.isEmpty(result)) {
    result.status = RefData.Model.hydrate(exportTask.status);
    result.typeRef = RefData.Model.hydrate(exportTask.typeRef);
  }

  return Immutable(result, { prototype: model });
}

/**
 * Transforms a backend export object into a frontend ExportTask Model
 * @param {Object} exportTask - backend formatted export
 * @returns {ExportTask}
 */
function deserialize(exportTask) {
  const result = _.pick(exportTask, _keys);
  return create(result, model);
}

/**
 * Transform a ExportTaskModel into a backend export object
 * @returns {Object}
 */
function serialize(exportTask) {
  const result = exportTask.asMutable({ deep: true });

  result.status = exportTask.status.id;
  result.typeRef = exportTask.typeRef.id;

  return _.pick(result, _keys);
}

const model = {
  get dateFinishedLabel() {
    return `Generated ${moment
      .tz(this.dateFinish, App.globalContext.userTimeZone.name)
      .format('dddd, MMMM Do YYYY [at] h:mma')} with ${this.currentNumRows} entries`;
  },
  get previousExportLabel() {
    return `Download previous export generated on ${moment
      .tz(this.dateFinish, App.globalContext.userTimeZone.name)
      .format('dddd, MMMM Do YYYY [at] h:mma')} with ${this.currentNumRows} entries`;
  },
  get pollingInterval() {
    return pollingInterval[this.size.toUpperCase()];
  },
  get taskProgressMessaging() {
    return exportTaskProgress[this.size.toUpperCase()];
  },
  get isInProgress() {
    return _.includes(
      [RefData.Enum.STATUS.EXPORT.INPROGRESS, RefData.Enum.STATUS.EXPORT.SCHEDULED],
      _.get(this, 'status.id', '')
    );
  },
  get isFailed() {
    return _.get(this, 'status.id', '') === RefData.Enum.STATUS.EXPORT.FAILED;
  },
  get isFinished() {
    return _.get(this, 'status.id', '') === RefData.Enum.STATUS.EXPORT.FINISHED;
  },
  get isEmpty() {
    return _.get(this, 'status.id', '') === RefData.Enum.STATUS.EXPORT.EMPTY;
  },
  get hasEntries() {
    return this.currentNumRows ? this.currentNumRows > 0 : false;
  },
  get statusCode() {
    return _.get(this, 'status.code', 'empty');
  },
  get downloadFileUrl() {
    return _.get(this, 'fileUrl', '');
  },
};

const _keys = [
  'allowExport',
  'currentNumRows',
  'dateCreated',
  'dateFinish',
  'exportId',
  'fileUrl',
  'id',
  'numRows',
  'password',
  'processTime',
  'size',
  'status',
  'typeRef',
];

export default {
  create,
  deserialize,
  serialize,
};
